import { memo } from 'react';
import styled from 'styled-components';
import { LuSave, LuFileDown } from 'react-icons/lu';
import {
	SidebarSection,
	SectionTitle,
	SectionContent,
	ActionButton,
	PolygonInfo,
} from './FlightPlannerStyles';
import { FEATURE_FLAGS } from '@constants/featureFlags';

const KmlButton = styled.button`
	background: rgba(255, 255, 255, 0.06);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 6px;
	padding: 4px 6px;
	cursor: pointer;
	color: rgba(255, 255, 255, 0.7);
	display: flex;
	align-items: center;
	transition: all 0.2s ease;

	&:hover {
		color: #34d399;
		border-color: rgba(52, 211, 153, 0.4);
	}
`;

/**
 * Step 4 — Export / Save flight plan panel.
 * Shows a summary of the plan and save/download actions.
 */
const WorkflowExport = ({
	projectSettings,
	flightType,
	dspSettings,
	dspLensName,
	waypointCount,
	formattedArea,
	dspAltitude,
	droneName,
	isSaving,
	saveMessage,
	editingPlanId,
	onSave,
	onDownloadKML,
}) => (
	<SidebarSection style={{ position: 'relative' }}>
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
			}}>
			<SectionTitle style={{ marginBottom: 0 }}>Export Flight Plan</SectionTitle>
			<KmlButton
				onClick={onDownloadKML}
				title="Export flight plan as KML file for use in Google Earth or other GIS tools">
				<LuFileDown size={14} />
			</KmlButton>
		</div>
		<SectionContent>
			<p style={{ marginBottom: '1rem' }}>
				Flight plan "{projectSettings.name || 'Untitled'}" is ready for export.
			</p>

			<PolygonInfo style={{ marginBottom: '1rem' }}>
				<div className="info-row">
					<span className="label">Type:</span>
					<span className="value">{flightType === 'dsp' ? 'DSP' : 'Orthomosaic'}</span>
				</div>
				<div className="info-row">
					<span className="label">Area:</span>
					<span className="value">{formattedArea}</span>
				</div>
				{flightType === 'dsp' ? (
					<>
						<div className="info-row">
							<span className="label">Drone:</span>
							<span className="value">{droneName}</span>
						</div>
						<div className="info-row">
							<span className="label">Lens:</span>
							<span className="value">{dspLensName}</span>
						</div>
						<div className="info-row">
							<span className="label">Plot Area:</span>
							<span className="value">{dspSettings.plotArea} m²</span>
						</div>
						<div className="info-row">
							<span className="label">Altitude:</span>
							<span className="value">{dspAltitude}m</span>
						</div>
						<div className="info-row">
							<span className="label">Waypoints:</span>
							<span className="value">{waypointCount}</span>
						</div>
						<div className="info-row">
							<span className="label">Speed:</span>
							<span className="value">{dspSettings.speed} m/s</span>
						</div>
						<div className="info-row">
							<span className="label">Gimbal:</span>
							<span className="value">{dspSettings.gimbalPitch}°</span>
						</div>
					</>
				) : (
					<>
						<div className="info-row">
							<span className="label">Drone:</span>
							<span className="value">{droneName}</span>
						</div>
						<div className="info-row">
							<span className="label">Height:</span>
							<span className="value">{projectSettings.flightHeight}m</span>
						</div>
						<div className="info-row">
							<span className="label">Overlap:</span>
							<span className="value">
								{projectSettings.sideOverlap}% / {projectSettings.frontOverlap}%
							</span>
						</div>
						<div className="info-row">
							<span className="label">Speed:</span>
							<span className="value">{projectSettings.speed} m/s</span>
						</div>
						{projectSettings.terrainFollow && (
							<div className="info-row">
								<span className="label">Terrain Follow:</span>
								<span className="value">On</span>
							</div>
						)}
						{FEATURE_FLAGS.SMART_OBLIQUE_ENABLED && projectSettings.smartOblique && (
							<div className="info-row">
								<span className="label">Smart Oblique:</span>
								<span className="value">On</span>
							</div>
						)}
					</>
				)}
			</PolygonInfo>

			{saveMessage && (
				<div
					style={{
						padding: '0.75rem',
						marginBottom: '0.75rem',
						borderRadius: '10px',
						background:
							saveMessage.type === 'error'
								? 'rgba(248, 113, 113, 0.1)'
								: 'rgba(52, 211, 153, 0.1)',
						border: `1px solid ${saveMessage.type === 'error' ? 'rgba(248, 113, 113, 0.2)' : 'rgba(52, 211, 153, 0.2)'}`,
						color: saveMessage.type === 'error' ? '#f87171' : '#34d399',
						fontSize: '0.85rem',
					}}>
					{saveMessage.text}
				</div>
			)}

			<ActionButton
				style={{
					marginBottom: '0.5rem',
				}}
				onClick={onSave}
				disabled={isSaving || !projectSettings.name?.trim()}>
				{isSaving ? (
					'Saving...'
				) : editingPlanId ? (
					<>
						<LuSave size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{' '}
						Update Flight Route
					</>
				) : (
					<>
						<LuSave size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{' '}
						Save Flight Route
					</>
				)}
			</ActionButton>
		</SectionContent>
	</SidebarSection>
);

export default memo(WorkflowExport);
